import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import {
  FieldReadable,
  GroupReadable,
  ReportFilterReadable,
  reportsAggTypesRetrieveOptions,
  reportsChartRetrieveQueryKey,
  reportsDestroyMutation,
  reportsDuplicateCreateMutation,
  reportsListQueryKey,
  reportsModelFieldsRetrieveOptions,
  reportsRetrieveOptions,
  reportsRetrieveQueryKey,
  reportsTableRetrieveQueryKey,
  reportsUpdateMutation,
  ValuesReadable
} from '@crm/api'
import { ConfirmModal, DynamicForm } from '@crm/components'
import {
  input_checkbox,
  input_operator,
  input_select,
  input_text,
  input_value
} from '@crm/components/DynamicForm/utils'
import { DynamicChart, DynamicTable } from '@crm/features/reports/components'
import { ChartTypes } from '@crm/features/reports/components/DynamicChart/types'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import { Report } from '@crm/types'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  Divider,
  Form,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Typography
} from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { FieldsCard } from './FieldsCard'

const { Title } = Typography

type FieldType =
  | FieldReadable
  | ReportFilterReadable
  | GroupReadable
  | ValuesReadable

enum EditTypes {
  FIELDS = 'fields',
  FILTERS = 'filters',
  GROUPS = 'groups',
  VALUES = 'values'
}

export const ReportEditor = () => {
  const queryClient = useQueryClient()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const navigate = useAppNavigate()
  const { t } = useLocales()
  const { id: reportId } = useParams()
  const [form] = Form.useForm()

  const [isOpen, setIsOpen] = useState<EditTypes | undefined>()
  const [editIndex, setEditIndex] = useState<number>()
  const [isSave, setIsSave] = useState(false)
  const [fields, setFields] = useState<FieldReadable[]>([])
  const [filters, setFilters] = useState<ReportFilterReadable[]>([])
  const [groups, setGroups] = useState<GroupReadable[]>([])
  const [values, setValues] = useState<ValuesReadable[]>([])
  const [deleteItem, setDeleteItem] = useState(false)

  const { data: fieldsData } = useQuery({
    ...reportsModelFieldsRetrieveOptions({
      path: {
        id: Number(reportId)
      }
    }),
    select: (data) => data?.fields
  })

  const { data: aggData } = useQuery({
    ...reportsAggTypesRetrieveOptions(),
    select: ({ results }) => results.map((value) => ({ value, text: value }))
  })

  const { data: report } = useQuery({
    ...reportsRetrieveOptions({
      path: {
        id: Number(reportId)
      }
    }),
    enabled: !!reportId
  })

  useEffect(() => {
    if (report) {
      form.setFieldsValue({ chart_type: report?.chart_type })

      setFields(report?.fields || [])
      setFilters(report?.filters || [])
      setGroups(report?.groups || [])
      setValues(report?.values || [])
    }
  }, [report, form])

  const mutation = useMutation({
    ...reportsUpdateMutation(),
    onError: () =>
      notification.error({
        message: t('error.error'),
        description: t('error.something_is_wrong')
      }),
    onSuccess: () => {
      invalidateQueries([
        ...reportsRetrieveQueryKey({
          path: {
            id: Number(reportId)
          }
        }),
        ...reportsTableRetrieveQueryKey({
          path: {
            id: Number(reportId)
          }
        }),
        ...reportsChartRetrieveQueryKey({
          path: {
            id: Number(reportId)
          }
        })
      ])
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const deleteReportMutation = useMutation({
    ...reportsDestroyMutation(),
    onError: () =>
      notification.error({
        message: t('error.error'),
        description: t('error.something_is_wrong')
      }),

    onSuccess: () => {
      invalidateQueries([reportsListQueryKey()])
      setDeleteItem(false)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })

      const report = queryClient
        .getQueryData<Report[]>(reportsListQueryKey())
        ?.find(({ id }) => id?.toString() === reportId)

      if (report) {
        navigate(paths.reportManagement.item(String(report?.id)))
      }
    }
  })

  const duplicate = useMutation({
    ...reportsDuplicateCreateMutation(),
    onError: () =>
      notification.error({
        message: t('error.error'),
        description: t('error.something_is_wrong')
      }),
    onSuccess: () => {
      invalidateQueries([reportsListQueryKey()])
      notification.success({
        message: t('success.success'),
        description: t('success.success_duplicate_report')
      })
    }
  })

  const onDuplicateReport = () =>
    duplicate.mutate({ path: { id: Number(reportId) } })

  const queryData = (
    queryClient.getQueryState(reportsListQueryKey())?.data as Report[]
  )?.find(({ id }) => id?.toString() === reportId)

  const formattedData = {
    fields: fields.map(({ label, field_name }) => ({
      label,
      field_name
    })),
    filters: filters.map(
      ({ label, field_name, field_type, filter_operator, filter_value }) => ({
        label,
        field_name,
        field_type,
        filter_operator,
        filter_value: Array.isArray(filter_value)
          ? filter_value
          : filter_value
          ? [filter_value]
          : null
      })
    ),
    groups: groups.map(({ label, field_name }) => ({
      label,
      field_name
    })),
    values: values.map(
      ({ label, field_name, function_type, show_in_chart }) => ({
        label,
        field_name,
        function_type,
        show_in_chart
      })
    ),
    id: Number.parseInt(reportId as string)
  }

  const onSubmit = () => {
    const values = form.getFieldsValue()
    mutation.mutate({
      path: {
        id: Number(reportId)
      },
      body: {
        ...queryData,
        ...values,
        ...formattedData
      }
    })
  }

  const onChange = () => {
    setEditIndex(undefined)
    setIsOpen(undefined)
  }

  useEffect(() => {
    if (isSave) {
      onChange()
      setIsSave(false)
    }
  }, [isSave, fields, filters, groups, values])

  const onDelete = () => {
    if (deleteItem && reportId) {
      deleteReportMutation.mutate({ path: { id: Number(reportId) } })
    }
  }

  const options = useMemo(
    () =>
      [...(fieldsData ?? [])].map(({ name: value, label: text, type }) => ({
        value,
        text,
        type
      })),
    [fieldsData]
  )

  const isEdit = useMemo(
    () => isOpen && editIndex !== undefined,
    [editIndex, isOpen]
  )

  const forms = {
    [EditTypes.FIELDS]: {
      elements: [
        input_text('label', t('generic.name')),
        input_select('field_name', t('generic.field'), options)
      ],
      onSuccess: (data: FieldReadable) => {
        setFields((prevState) =>
          isEdit
            ? [...prevState].map((prevData, i) =>
                i === editIndex ? data : prevData
              )
            : [...prevState, data]
        )
        setIsSave(true)
      }
    },
    [EditTypes.FILTERS]: {
      elements: [
        input_text('label', t('generic.name')),
        input_select('field_name', t('generic.field'), options),
        input_operator(
          'filter_operator',
          t('generic.filter_operator'),
          { col: 12 },
          undefined,
          null,
          false
        ),
        input_value(
          'filter_value',
          t('generic.filter_value'),
          { col: 12 },
          undefined,
          null,
          false
        )
      ],
      onSuccess: (data: ReportFilterReadable) => {
        setFilters((prevState) =>
          isEdit
            ? prevState.map((prevData, i) =>
                i === editIndex ? data : prevData
              )
            : [...prevState, data]
        )
        setIsSave(true)
      }
    },
    [EditTypes.GROUPS]: {
      elements: [
        input_text('label', t('generic.name')),
        input_select('field_name', t('generic.field'), options)
      ],
      onSuccess: (data: GroupReadable) => {
        setGroups((prevState) =>
          isEdit
            ? prevState.map((prevData, i) =>
                i === editIndex ? data : prevData
              )
            : [...prevState, data]
        )
        setIsSave(true)
      }
    },
    [EditTypes.VALUES]: {
      elements: [
        input_text('label', t('generic.name')),
        input_select('field_name', t('generic.field'), options),
        input_select('function_type', t('generic.function_type'), aggData),
        input_checkbox('show_in_chart', t('generic.show_in_chart'))
      ],
      onSuccess: (data: ValuesReadable) => {
        setValues((prevState) =>
          isEdit
            ? prevState.map((prevData, i) =>
                i === editIndex ? data : prevData
              )
            : [...prevState, data]
        )
        setIsSave(true)
      }
    }
  }

  const onEdit = (index: number, type: EditTypes) => {
    setEditIndex(index)
    setIsOpen(type)
  }

  const initialValues = useMemo(() => {
    switch (isOpen) {
      case EditTypes.FIELDS:
        return fields?.find((_, i) => i === editIndex)
      case EditTypes.FILTERS:
        return filters?.find((_, i) => i === editIndex)
      case EditTypes.GROUPS:
        return groups?.find((_, i) => i === editIndex)
      case EditTypes.VALUES:
        return values?.find((_, i) => i === editIndex)
      default:
        return undefined
    }
  }, [isOpen, editIndex, fields, filters, groups, values])

  const onFieldsSortEnd = (action: (state: FieldType[]) => FieldType[]) =>
    setFields(action(fields) as FieldReadable[])

  const onFiltersSortEnd = (action: (state: FieldType[]) => FieldType[]) =>
    setFilters(action(filters) as ReportFilterReadable[])

  const onGroupsSortEnd = (action: (state: FieldType[]) => FieldType[]) =>
    setGroups(action(groups) as GroupReadable[])

  const onValuesSortEnd = (action: (state: FieldType[]) => FieldType[]) =>
    setValues(action(values) as ValuesReadable[])

  return (
    <>
      <Form form={form} onValuesChange={onChange} onFinish={onSubmit}>
        <Space className="mb-30">
          <Button type="primary" htmlType="submit">
            {t('buttons.save_refresh')}
          </Button>

          <Button onClick={() => onDuplicateReport()}>
            {t('buttons.duplicate')}
          </Button>

          <Button type="text" onClick={() => setDeleteItem(true)}>
            {t('buttons.delete')}
          </Button>
        </Space>

        <Row gutter={[12, 12]} className="mb-20">
          <Col sm={24} md={12}>
            <FieldsCard
              title={t(`generic.${EditTypes.FIELDS}`)}
              fields={fields.map((field, i) => ({
                id: field.id || i,
                ...field
              }))}
              onRemove={(index) => {
                setFields((prevState) =>
                  prevState.filter((_, i) => i !== index)
                )
                setIsSave(true)
              }}
              onAdd={() => {
                setIsOpen(EditTypes.FIELDS)
              }}
              onEdit={(index) => onEdit(index, EditTypes.FIELDS)}
              onSortEnd={onFieldsSortEnd}
            />
          </Col>

          <Col sm={24} md={12}>
            <FieldsCard
              title={t(`generic.${EditTypes.FILTERS}`)}
              fields={filters?.map((field, i) => ({
                id: field.id || i,
                ...field
              }))}
              onRemove={(index) => {
                setFilters((prevState) =>
                  prevState.filter((_, i) => i !== index)
                )
                setIsSave(true)
              }}
              onAdd={() => {
                setIsOpen(EditTypes.FILTERS)
              }}
              onEdit={(index) => onEdit(index, EditTypes.FILTERS)}
              onSortEnd={onFiltersSortEnd}
              renderItem={(field) => {
                if ('field_type' in field) {
                  return (
                    <>
                      {field.label}{' '}
                      {field?.field_type && `(${field.field_type?.type})`}
                    </>
                  )
                }
                return field.label
              }}
            />
          </Col>

          <Col sm={24} md={12}>
            <FieldsCard
              title={t(`generic.${EditTypes.GROUPS}`)}
              fields={groups.map((field, i) => ({
                id: field.id || i,
                ...field
              }))}
              onRemove={(index) => {
                setGroups((prevState) =>
                  prevState.filter((_, i) => i !== index)
                )
                setIsSave(true)
              }}
              onAdd={() => {
                setIsOpen(EditTypes.GROUPS)
              }}
              onEdit={(index) => onEdit(index, EditTypes.GROUPS)}
              onSortEnd={onGroupsSortEnd}
            />
          </Col>

          <Col sm={24} md={12}>
            <FieldsCard
              title={t(`generic.${EditTypes.VALUES}`)}
              fields={values.map((field, i) => ({
                id: field.id || i,
                ...field
              }))}
              onRemove={(index) => {
                setValues((prevState) =>
                  prevState.filter((_, i) => i !== index)
                )
                setIsSave(true)
              }}
              onAdd={() => {
                setIsOpen(EditTypes.VALUES)
              }}
              onEdit={(index) => onEdit(index, EditTypes.VALUES)}
              renderItem={(field, i) => {
                if ('show_in_chart' in field && 'function_type' in field) {
                  return (
                    <Space>
                      <Button
                        icon={
                          field.show_in_chart ? (
                            <EyeOutlined />
                          ) : (
                            <EyeInvisibleOutlined />
                          )
                        }
                        onClick={() =>
                          setValues((prevState) =>
                            prevState.map((value, index) =>
                              i === index
                                ? {
                                    ...value,
                                    show_in_chart: !value.show_in_chart
                                  }
                                : value
                            )
                          )
                        }
                      />
                      <>
                        {field.label}{' '}
                        {field?.function_type && `(${field?.function_type})`}
                      </>
                    </Space>
                  )
                }
                return field.label
              }}
              onSortEnd={onValuesSortEnd}
            />
          </Col>
        </Row>

        <Form.Item name="chart_type" label={t('generic.chart')}>
          <Select
            placeholder={t('generic.chart_type')}
            options={[
              { label: 'Bar', value: ChartTypes.BAR },
              { label: 'Line', value: ChartTypes.LINE },
              { label: 'Pie', value: ChartTypes.PIE },
              { label: 'Funnel', value: ChartTypes.FUNNEL }
            ]}
          />
        </Form.Item>
      </Form>

      <Divider />

      <Title level={5}>{t('generic.preview')}:</Title>

      {report?.chart_type && (
        <>
          <DynamicTable id={reportId} />

          {!!groups?.length && !!values?.length && (
            <>
              <Divider />

              <DynamicChart
                chartType={report?.chart_type as ChartTypes}
                id={reportId}
                label="Chart"
                xAxy="name"
              />
            </>
          )}
        </>
      )}

      {isOpen && (
        <Modal
          open={!!isOpen}
          maskClosable={false}
          title={t(`generic.${isOpen}`)}
          onCancel={onChange}
          okText={t('buttons.save')}
          cancelText={t('buttons.cancel')}
          okButtonProps={{ htmlType: 'submit', form: isOpen }}
          destroyOnHidden
        >
          <DynamicForm
            formName={isOpen}
            initialValues={initialValues}
            elements={forms[isOpen].elements}
            onSubmit={
              forms[isOpen].onSuccess as (
                values:
                  | FieldReadable
                  | ReportFilterReadable
                  | GroupReadable
                  | ValuesReadable
              ) => void
            }
          />
        </Modal>
      )}

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={() => setDeleteItem(false)}
      />
    </>
  )
}
